import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { useThematicSeeds } from '@/hooks/useThematicSeeds';
import { useThematicNodes } from '@/hooks/useThematicNodes';
import { useThematicEntities } from '@/hooks/useThematicEntities';
import { useThematicRelations } from '@/hooks/useThematicRelations';
import type { ThematicMap } from '@/types/thematicMaps';

interface ExportDialogProps {
  map: ThematicMap;
}

export function ExportDialog({ map }: ExportDialogProps) {
  const [open, setOpen] = useState(false);
  const { seeds } = useThematicSeeds(map.id);
  const { nodes } = useThematicNodes(map.id);
  const { entities } = useThematicEntities(map.id);
  const { relations } = useThematicRelations(map.id);

  const exportJSON = () => {
    const data = {
      map: {
        id: map.id,
        name: map.name,
        macro_statement: map.macro_statement,
        scope_in: map.scope_in,
        scope_out: map.scope_out,
        audience: map.audience,
        language: map.language,
        map_version: map.map_version,
        status: map.status,
      },
      seeds: seeds.map(s => ({
        id: s.id,
        name: s.name,
        slug: s.slug,
        intent_family: s.intent_family,
        priority: s.priority,
      })),
      nodes: nodes.map(n => ({
        id: n.id,
        seed_id: n.seed_id,
        title: n.title,
        slug: n.slug,
        document_role: n.document_role,
        primary_intent: n.primary_intent,
        primary_entity_raw: n.primary_entity_raw,
        required_entities_raw: n.required_entities_raw,
        canonical_question: n.canonical_question,
        node_status: n.node_status,
      })),
      entities: entities.map(e => ({
        id: e.id,
        canonical_name: e.canonical_name,
        entity_type: e.entity_type,
        aliases: e.aliases,
        description: e.description,
      })),
      relations: relations.map(r => ({
        from_node_id: r.from_node_id,
        to_node_id: r.to_node_id,
        relation_type: r.relation_type,
        weight: r.weight,
      })),
    };

    downloadFile(
      JSON.stringify(data, null, 2),
      `${map.name.replace(/\s+/g, '_')}_export.json`,
      'application/json'
    );
  };

  const exportCSV = () => {
    const seedMap = new Map(seeds.map(s => [s.id, s]));
    const headers = [
      'seed_name', 'seed_slug', 'title', 'slug', 'document_role',
      'primary_intent', 'primary_entity_raw', 'canonical_question',
      'node_status', 'required_entities_raw',
    ];

    const rows = nodes.map(n => {
      const seed = seedMap.get(n.seed_id);
      return [
        seed?.name || '',
        seed?.slug || '',
        n.title,
        n.slug,
        n.document_role,
        n.primary_intent,
        n.primary_entity_raw,
        n.canonical_question,
        n.node_status,
        (n.required_entities_raw || []).join('; '),
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    downloadFile(csv, `${map.name.replace(/\s+/g, '_')}_nodes.csv`, 'text/csv');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Download className="mr-2 h-3.5 w-3.5" />
          Eksportuj
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Eksportuj mapę: {map.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Button className="w-full justify-start" variant="outline" onClick={exportJSON}>
            <Download className="mr-2 h-4 w-4" />
            Eksportuj JSON (pełna mapa)
          </Button>
          <Button className="w-full justify-start" variant="outline" onClick={exportCSV}>
            <Download className="mr-2 h-4 w-4" />
            Eksportuj CSV (flat — 1 wiersz per node)
          </Button>
          <p className="text-xs text-muted-foreground">
            JSON: macro + seeds + nodes + entities + relations.
            CSV: flat tabela z seed_name, entity, role, question, status.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
